import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { uniqBy } from 'lodash-es';
import type { OverlappingCalendarEvent, ScheduleRequestEvent } from '@frontend/schedule-helpers';
import { createShallowStore, createStore } from '@frontend/store';
import { sentry } from '@frontend/tracking';
import { useCalendarDeepLinkParams } from '../hooks/calendar/use-calendar-deep-link-params';
import { convertScheduleRequestEventToRequest } from '../hooks/scheduler-v3/use-convert-schedule-request-to-event';
import {
  useGetSelectedCalendarDateAppointmentInfo,
  useGetSelectedCalendarDateAppointmentList,
} from '../hooks/use-get-selected-calendar-date-appointment-info';
import { ExtendedAppointment } from '../types';

export type AppointmentDetailsEntryPoint = 'schedule' | 'schedule_activity' | 'patient_profile';

type State = {
  appointmentDetailsEntryPoint: AppointmentDetailsEntryPoint;
  insuranceStatusCalendarDate?: string;
  selectedAppointments: ExtendedAppointment[];
  selectedAppointment?: ExtendedAppointment;
  selectedAppointmentId?: string;
  selectedRequests: ScheduleRequest[];
  selectedRequest?: ScheduleRequest;
  selectedRequestId?: string;
};

type Actions = {
  setSelectedAppointments: (appointments: OverlappingCalendarEvent[], insuranceStatusCalendarDate: string) => void;
  updateSelectedAppointment: (appointment: ExtendedAppointment) => void;
  setSelectedAppointment: (
    appointment: ExtendedAppointment,
    reset?: boolean,
    entryPoint?: AppointmentDetailsEntryPoint
  ) => void;
  setSelectedAppointmentId: (appointmentId: string, entryPoint?: AppointmentDetailsEntryPoint) => void;
  resetSelectedAppointment: () => void;
  resetSelection: () => void;
  setSelectedRequestId: (requestId: string) => void;
  resetSelectedRequest: () => void;
  resetRequests: () => void;
  setSelectedRequest: (request: ScheduleRequest, reset?: boolean) => void;
};

const initialState: State = {
  appointmentDetailsEntryPoint: 'schedule',
  insuranceStatusCalendarDate: undefined,
  selectedAppointments: [],
  selectedRequests: [],
};

type ScheduleAppointmentInfoPanelStore = State & Actions;

const useStore = createStore<ScheduleAppointmentInfoPanelStore>(
  (set) => ({
    ...initialState,
    setSelectedAppointments: (appointments, insuranceStatusCalendarDate) => {
      const requests = uniqBy(
        (appointments.filter((appt) => appt.type === 'request') as ScheduleRequestEvent[])
          .map((app) => convertScheduleRequestEventToRequest(app.request))
          .filter(Boolean) as ScheduleRequest[],
        'id'
      );
      const selectedAppointments = appointments.filter((appt) => appt.type !== 'request');

      set({
        appointmentDetailsEntryPoint: 'schedule',
        insuranceStatusCalendarDate,
        selectedAppointments,
        // auto select appointment if only one is selected
        selectedAppointment: selectedAppointments.length === 1 ? selectedAppointments[0] : undefined,
        selectedAppointmentId: selectedAppointments.length === 1 ? selectedAppointments[0].id : undefined,
        selectedRequests: requests,
        selectedRequest: requests.length === 1 ? requests[0] : undefined,
        selectedRequestId: requests.length === 1 ? requests[0].id : undefined,
      });
    },
    updateSelectedAppointment: (appointment) => set({ selectedAppointment: appointment }),
    setSelectedAppointment: (appointment, reset = false, entryPoint = 'schedule') => {
      if (reset) {
        set({
          appointmentDetailsEntryPoint: entryPoint,
          selectedAppointment: appointment,
          selectedAppointmentId: appointment.id,
          selectedAppointments: [],
          insuranceStatusCalendarDate: undefined,
        });
      } else {
        set({
          appointmentDetailsEntryPoint: entryPoint,
          selectedAppointment: appointment,
          selectedAppointmentId: appointment.id,
        });
      }
    },
    setSelectedRequest: (request, reset = false) => {
      if (reset) {
        set({
          selectedRequest: request,
          selectedRequestId: request.id,
          selectedRequests: [],
        });
      } else {
        set({ selectedRequest: request, selectedRequestId: request.id });
      }
    },
    setSelectedAppointmentId: (appointmentId, entryPoint = 'schedule') => {
      set({
        appointmentDetailsEntryPoint: entryPoint,
        selectedAppointmentId: appointmentId,
        selectedAppointment: undefined,
        selectedAppointments: [],
        insuranceStatusCalendarDate: undefined,
      });
    },
    setSelectedRequestId: (requestId) => {
      set({
        selectedRequestId: requestId,
        selectedRequest: undefined,
        selectedRequests: [],
      });
    },
    resetSelectedAppointment: () =>
      set({
        appointmentDetailsEntryPoint: 'schedule',
        selectedAppointment: undefined,
        selectedAppointmentId: undefined,
      }),
    resetSelection: () =>
      set({
        appointmentDetailsEntryPoint: 'schedule',
        selectedAppointments: [],
        selectedAppointment: undefined,
        selectedAppointmentId: undefined,
        insuranceStatusCalendarDate: undefined,
        selectedRequests: [],
        selectedRequest: undefined,
        selectedRequestId: undefined,
      }),
    resetRequests: () => set({ selectedRequests: [], selectedRequest: undefined, selectedRequestId: undefined }),
    resetSelectedRequest: () => set({ selectedRequest: undefined, selectedRequestId: undefined }),
  }),
  {
    name: 'ScheduleAppointmentInfoPanelStore',
    enabled: true,
    trace: true,
  }
);

export const useScheduleAppointmentInfoPanelShallowStore = createShallowStore(useStore);

export const useSetCalendarAppointmentInfoAsSelected = (
  eventId: string,
  locationId: string,
  cachedAppointments?: ExtendedAppointment[],
  entryPoint: AppointmentDetailsEntryPoint = 'schedule'
) => {
  const {
    selectedAppointment,
    selectedAppointmentId,
    updateSelectedAppointment,
    setSelectedAppointment,
    setSelectedAppointmentId,
    resetSelection,
  } = useScheduleAppointmentInfoPanelShallowStore(
    'selectedAppointment',
    'selectedAppointmentId',
    'updateSelectedAppointment',
    'setSelectedAppointment',
    'setSelectedAppointmentId',
    'resetSelection'
  );
  // get appointment list based on calendar context & cached queries
  const apptInfoFromCalendar = useGetSelectedCalendarDateAppointmentInfo(eventId);

  // Memoize apptInfoFromCache to prevent creating new object references on every render
  const apptInfoFromCache = useMemo(() => {
    if (apptInfoFromCalendar) {
      return apptInfoFromCalendar;
    }
    return cachedAppointments?.find((appt) => appt.id === eventId);
    // SP-TODO: Find better way to handle deps here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(apptInfoFromCalendar), JSON.stringify(cachedAppointments), eventId]);

  const isSelected = selectedAppointmentId === eventId;

  // need to sync selected appointment info if selected to keep data consistent on updates outside of this store
  useEffect(() => {
    if (isSelected && apptInfoFromCache) {
      updateSelectedAppointment(apptInfoFromCache);
    }
  }, [apptInfoFromCache, isSelected, updateSelectedAppointment]);

  return {
    isSelected,
    selectedAppointment,
    setAppointment: (reset?: boolean) => {
      if (apptInfoFromCache) {
        setSelectedAppointment(apptInfoFromCache, reset, entryPoint);
      } else {
        console.error('[useSetCalendarAppointmentInfoAsSelected] appointment info not found.');
        sentry.error({
          topic: 'schedule-calendar',
          team: 'schedule',
          error: `Appointment info not found.`,
          addContext: { name: 'details', context: { eventId, locationId } },
        });
        setSelectedAppointmentId(eventId, entryPoint);
      }
    },
    resetSelection,
  };
};

export const useSetCalendarAppointmentListAsSelected = (eventIds: string[], calendarDate: string) => {
  const { setSelectedAppointments, selectedAppointments } = useScheduleAppointmentInfoPanelShallowStore(
    'setSelectedAppointments',
    'selectedAppointments'
  );
  // get appointment list based on calendar context & cached queries
  const appointments = useGetSelectedCalendarDateAppointmentList(eventIds);

  const selectedAppointmentIds = useMemo(() => selectedAppointments.map((appt) => appt.id), [selectedAppointments]);

  const hasSelectedAppointmentsInList = useMemo(
    () => eventIds.some((eventId) => selectedAppointmentIds.includes(eventId)),
    [eventIds, selectedAppointmentIds]
  );

  const shouldSyncSelection = selectedAppointmentIds?.length !== eventIds?.length;

  // on resize of calendar view, sync current overlapping events
  useEffect(() => {
    if (hasSelectedAppointmentsInList && shouldSyncSelection) {
      setSelectedAppointments(appointments, calendarDate);
    }
  }, [appointments, hasSelectedAppointmentsInList, shouldSyncSelection, setSelectedAppointments, calendarDate]);

  const setAppointments = useCallback(
    () => setSelectedAppointments(appointments, calendarDate),
    [setSelectedAppointments, appointments, calendarDate]
  );

  return {
    isSelected: hasSelectedAppointmentsInList,
    selectedAppointments,
    setAppointments,
  };
};

/**
 * Use this hook when the calendar grid (V2/V3) reports selection changes via `onEventsSelect`.
 * Pass the returned `handleEventsSelect` directly as the `onEventsSelect` prop.
 */
export const useSetCalendarGridEventsAsSelected = (calendarDate: string) => {
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
  const hasPendingSync = useRef(false);
  const { setSelectedAppointments } = useScheduleAppointmentInfoPanelShallowStore('setSelectedAppointments');
  const { deepLinkedEventId } = useCalendarDeepLinkParams();

  // get appointment list based on calendar context & cached queries
  const appointments = useGetSelectedCalendarDateAppointmentList(selectedEventIds);

  useEffect(() => {
    if (hasPendingSync.current && appointments.length) {
      setSelectedAppointments(appointments, calendarDate);
      hasPendingSync.current = false;
    }
  }, [appointments, calendarDate, setSelectedAppointments]);

  useEffect(() => {
    if (deepLinkedEventId) {
      setSelectedEventIds([deepLinkedEventId]);
    }
  }, [deepLinkedEventId]);

  const handleEventsSelect = (eventIds: string[]) => {
    hasPendingSync.current = true;
    setSelectedEventIds(eventIds);
  };

  return { handleEventsSelect, selectedEventIds, deepLinkedEventId };
};
